/**
 * Settings. Day 13, ported from the prototype's settings route and cut to
 * what this product now has.
 *
 *   /settings            Profile    name, college, year and city, editable
 *   /settings/plan       Plan       the plan and the invoices, and nothing to buy
 *   /settings/sign-in    Sign in    the address, the one device rule, sign out
 *   /settings/data       Your data  download it, delete the account, or keep it
 *
 * The prototype's Notifications tab is not here, because nothing sends a
 * notification a student could turn off yet, and a switch that does nothing is
 * a promise. Its device history went with the portal's own sessions (ADR-010).
 *
 * The address is not editable. It is the one Clerk verified and signs the
 * student in with, and the portal's copy of it has to match Clerk's: changing
 * it at Clerk alone would split the two, and a sign in with the new address
 * would find no account. Day 13 decided not to offer it (threat model, F8).
 * A student who needs it changed writes to the help address.
 */

import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { BillingResponse, Profile, StudentYear } from "@ls/shared";
import { PROGRAMMES, YEAR_LABELS, yearsOf, type ProgrammeId } from "@ls/shared/catalogue";
import { ApiUnavailable, download, expect, http, saveBlob } from "../api";
import { BRAND_NAME, HELP_EMAIL } from "../brand";
import {
  billingResponseContract,
  deletionResponseContract,
  profileErrorContract,
  profileResponseContract,
} from "../contracts.generated";
import { useSession } from "../shell/context";
import { planLine } from "../shell/Sidebar";
import type { PageProps } from "../shell/routes";
import { Button, Card, PageHeader, Select, Status, Table, Tabs } from "../ui";

type Tab = "profile" | "plan" | "sign-in" | "data";

const TABS: readonly { readonly id: Tab; readonly label: string; readonly path: string }[] = [
  { id: "profile", label: "Profile", path: "/settings" },
  { id: "plan", label: "Plan", path: "/settings/plan" },
  { id: "sign-in", label: "Sign in", path: "/settings/sign-in" },
  { id: "data", label: "Your data", path: "/settings/data" },
];

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export function formatRupees(paise: number): string {
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString("en-IN", { minimumFractionDigits: Number.isInteger(rupees) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

function unreachable(caught: unknown): string {
  return caught instanceof ApiUnavailable
    ? `${BRAND_NAME} could not be reached. Check your connection.`
    : "Something went wrong. Try again.";
}

/** The prototype's names for the same places, so an old link still lands. */
const OLD_NAMES: Readonly<Record<string, Tab>> = { security: "sign-in", subscription: "plan" };

export function Settings(props: PageProps) {
  const asked = props.params[0];
  const tab: Tab | null =
    asked === undefined ? "profile" : (TABS.find((t) => t.id === asked)?.id ?? OLD_NAMES[asked] ?? null);
  return (
    <div class="settings">
      <PageHeader title="Settings" />
      <Tabs label="Settings" current={tab} tabs={TABS.map((t) => ({ id: t.id, label: t.label, href: t.path }))} />
      <div class="settings-body">
        {tab === "profile" ? <ProfileTab /> : null}
        {tab === "plan" ? <PlanTab /> : null}
        {tab === "sign-in" ? <SignInTab /> : null}
        {tab === "data" ? <DataTab /> : null}
        {tab === null ? (
          <Card>
            <p>There is no such section of Settings.</p>
            <p class="note">
              <a href="/settings">Back to Settings</a>
            </p>
          </Card>
        ) : null}
      </div>
    </div>
  );
}

/** Facts about one thing: a term and its value, a hairline between (styles/base.css .facts). */
function Facts(props: { readonly rows: readonly (readonly [string, ComponentChildren])[] }) {
  return (
    <dl class="facts">
      {props.rows.map(([term, value]) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/* ------------------------------------------------------------------ */
/* Profile                                                             */
/* ------------------------------------------------------------------ */

function ProfileTab() {
  const session = useSession();
  const { profile } = session;
  const [name, setName] = useState(profile.name ?? "");
  const [college, setCollege] = useState(profile.college ?? "");
  // "" is an account from before programmes existed: it keeps three years until it chooses one.
  const [programme, setProgramme] = useState<ProgrammeId | "">(profile.programme ?? "");
  const [year, setYear] = useState<StudentYear>(profile.year ?? "1");
  const [city, setCity] = useState(profile.city ?? "");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: "ok" | "bad"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const changes: Partial<Record<"name" | "college" | "programme" | "year" | "city", string>> = {};
  if (name.trim() !== (profile.name ?? "")) changes.name = name.trim();
  if (college.trim() !== (profile.college ?? "")) changes.college = college.trim();
  if (programme !== "" && programme !== profile.programme) changes.programme = programme;
  if (year !== profile.year) changes.year = year;
  if (city.trim() !== (profile.city ?? "")) changes.city = city.trim();
  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    setMessage(null);
    setFields({});
    try {
      const reply = await http.patch("/profile", changes);
      if (reply.status === 200) {
        const saved = expect(reply, profileResponseContract, "/profile").profile;
        session.setProfile(saved);
        setName(saved.name ?? "");
        setCollege(saved.college ?? "");
        setCity(saved.city ?? "");
        setMessage({ tone: "ok", text: "Saved." });
        return;
      }
      if (reply.status === 401) {
        session.signOut();
        return;
      }
      const problem = profileErrorContract.safeParse(reply.body);
      if (problem.success && problem.data.fields) {
        setFields(problem.data.fields);
        setMessage({ tone: "bad", text: "Check the highlighted fields." });
        return;
      }
      setMessage({ tone: "bad", text: problem.success ? problem.data.message : "That was not saved. Try again." });
    } catch (caught) {
      setMessage({ tone: "bad", text: unreachable(caught) });
    } finally {
      setBusy(false);
    }
  };

  const input = (id: string, key: string, label: string, value: string, set: (v: string) => void, autocomplete: string) => (
    <div class="field">
      <label for={id}>{label}</label>
      <input
        class="input"
        id={id}
        autocomplete={autocomplete}
        required
        value={value}
        aria-invalid={fields[key] ? "true" : undefined}
        aria-describedby={fields[key] ? `${id}-error` : undefined}
        onInput={(event) => set((event.currentTarget as HTMLInputElement).value)}
      />
      {fields[key] ? (
        <span class="error" id={`${id}-error`}>
          {fields[key]}
        </span>
      ) : null}
    </div>
  );

  return (
    <Card title="Profile">
      <form
        class="stack settings-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {input("sname", "name", "Full name", name, setName, "name")}
        <p class="note">Printed on your certificates, so use the name on your college ID.</p>
        {input("scollege", "college", "College", college, setCollege, "organization")}
        <div class="grid g2">
          <div class="field">
            <label for="sprogramme">Programme</label>
            <Select
              id="sprogramme"
              value={programme}
              placeholder="Choose"
              invalid={Boolean(fields["programme"])}
              options={PROGRAMMES.map((option) => ({ value: option.id, label: option.label }))}
              onChange={(value) => {
                const next = value as ProgrammeId;
                setProgramme(next);
                if (!yearsOf(next).includes(year)) setYear(yearsOf(next).at(-1) as StudentYear);
              }}
            />
            {fields["programme"] ? <span class="error">{fields["programme"]}</span> : null}
          </div>
          <div class="field">
            <label for="syear">Year</label>
            <Select
              id="syear"
              value={year}
              invalid={Boolean(fields["year"])}
              options={(programme === "" ? yearsOf("3_year") : yearsOf(programme)).map((value) => ({ value, label: YEAR_LABELS[value] ?? value }))}
              onChange={(value) => setYear(value as StudentYear)}
            />
            {fields["year"] ? (
              <span class="error">{fields["year"]}</span>
            ) : programme === "" ? (
              <span class="hint">Choose your programme to unlock 4th and 5th year.</span>
            ) : null}
          </div>
        </div>
        {input("scity", "city", "City", city, setCity, "address-level2")}
        {message ? (
          <p class={message.tone === "bad" ? "notice bad" : "notice"} role={message.tone === "bad" ? "alert" : "status"}>
            {message.text}
          </p>
        ) : null}
        <div>
          <Button variant="primary" type="submit" disabled={busy || !dirty}>
            {busy ? "Saving..." : "Save changes"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Plan                                                                */
/* ------------------------------------------------------------------ */

function PlanTab() {
  const session = useSession();
  const [billing, setBilling] = useState<BillingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const reply = await http.get("/billing");
        if (reply.status === 401) {
          session.signOut();
          return;
        }
        const body = expect(reply, billingResponseContract, "/billing");
        if (live) setBilling(body);
      } catch (caught) {
        if (live) setError(unreachable(caught));
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (error) {
    return (
      <Card title="Plan">
        <p class="notice bad" role="alert">
          {error}
        </p>
      </Card>
    );
  }
  if (!billing) {
    return (
      <Card title="Plan">
        <p class="note" role="status">
          Loading your plan...
        </p>
      </Card>
    );
  }

  const plan = billing.plan;
  return (
    <div class="stack settings-stack">
      <Card title="Plan">
        <div class="stack">
          <div class="settings-plan">
            <h3>{planLine(session.plan)}</h3>
            <Status tone="ok">Active</Status>
          </div>
          {plan ? (
            <Facts
              rows={[
                ["Started", formatDate(plan.startedAt)],
                ["Ends", plan.expiresAt ? formatDate(plan.expiresAt) : "Does not end"],
                [
                  "How",
                  plan.source === "internship"
                    ? "The Standing Lawyers internship"
                    : plan.source === "firm_code"
                      ? "Your law firm's code"
                      : plan.source === "purchase"
                        ? plan.paidPaise
                          ? `Bought for ${formatRupees(plan.paidPaise)}`
                          : "A voucher that covered the whole price"
                        : "A voucher",
                ],
              ]}
            />
          ) : null}
          <div>
            <Button href="/subscriptions">{plan ? "Subscriptions" : "See the plans"}</Button>
          </div>
        </div>
      </Card>
      {billing.invoices.length === 0 ? (
        <Card title="Invoices">
          <p class="note">No invoices yet.</p>
        </Card>
      ) : (
        <Table
          caption="Invoices"
          columns={[
            { key: "number", label: "Invoice" },
            { key: "date", label: "Date" },
            { key: "for", label: "For" },
            { key: "amount", label: "Amount", numeric: true },
          ]}
          rows={billing.invoices.map((invoice) => ({
            number: <span class="mono">{invoice.number}</span>,
            date: formatDate(invoice.issuedAt),
            for: invoice.description,
            amount: formatRupees(invoice.totalPaise),
          }))}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Sign in                                                             */
/* ------------------------------------------------------------------ */

function SignInTab() {
  const { profile, signOut } = useSession();
  return (
    <Card title="Sign in">
      <div class="stack">
        <Facts rows={[["Address", profile.email]]} />
        <p class="note">You sign in with this address, by a code or with Google. To change it, write to {HELP_EMAIL} from it.</p>
        <p class="note">
          {BRAND_NAME} keeps you signed in on one device at a time. Signing in somewhere else signs this device out.
        </p>
        <div>
          <Button onClick={signOut}>Sign out</Button>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/* Your data                                                           */
/* ------------------------------------------------------------------ */

function DataTab() {
  const session = useSession();
  const { profile } = session;
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const exportData = async () => {
    setExporting(true);
    setExportMessage(null);
    try {
      const got = await download("/profile/export");
      if (got.status === 401) {
        session.signOut();
        return;
      }
      if (!got.blob) {
        setExportMessage(
          got.status === 429 ? "You have downloaded your data several times this hour. Try again later." : "That did not work. Try again.",
        );
        return;
      }
      saveBlob(got.blob, got.filename ?? "legal-studious-data.json");
      setExportMessage("Downloaded.");
    } catch (caught) {
      setExportMessage(unreachable(caught));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div class="stack settings-stack">
      <Card title="Download your data">
        <div class="stack">
          <p class="note">Everything {BRAND_NAME} holds about you, as one file, with a note on what deleting your account does to each part.</p>
          <div class="row">
            <Button disabled={exporting} onClick={() => void exportData()}>
              {exporting ? "Preparing..." : "Download my data"}
            </Button>
            {exportMessage ? (
              <span class="note" role="status">
                {exportMessage}
              </span>
            ) : null}
          </div>
        </div>
      </Card>
      {profile.deletion ? <PendingDeletion profile={profile} /> : <DeleteAccount />}
    </div>
  );
}

/** A deletion is scheduled: when, why, and the way back if there is one. */
function PendingDeletion(props: { profile: Profile }) {
  const session = useSession();
  const deletion = props.profile.deletion;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!deletion) return null;

  const keep = async () => {
    setBusy(true);
    setError(null);
    try {
      const reply = await http.post("/profile/delete/cancel");
      if (reply.status === 401) {
        session.signOut();
        return;
      }
      expect(reply, deletionResponseContract, "/profile/delete/cancel");
      session.setProfile({ ...props.profile, deletion: null });
    } catch (caught) {
      setError(unreachable(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Your account is due to be deleted">
      <div class="stack">
        <Facts
          rows={[
            ["Asked on", formatDate(deletion.requestedAt)],
            ["Deleted on", formatDate(deletion.dueAt)],
          ]}
        />
        {deletion.reason === "requested" ? (
          <>
            <p class="note">Until then everything works as normal, and you can change your mind.</p>
            {error ? (
              <p class="notice bad" role="alert">
                {error}
              </p>
            ) : null}
            <div>
              <Button variant="primary" disabled={busy} onClick={() => void keep()}>
                {busy ? "Keeping it..." : "Keep my account"}
              </Button>
            </div>
          </>
        ) : (
          <p class="note">Your plan ended. Taking a new plan keeps your account; until then, download anything you want to keep.</p>
        )}
      </div>
    </Card>
  );
}

function DeleteAccount() {
  const session = useSession();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const reply = await http.post("/profile/delete", { confirm: typed.trim() });
      if (reply.status === 401) {
        session.signOut();
        return;
      }
      if (reply.status !== 200) {
        const problem = profileErrorContract.safeParse(reply.body);
        setError(problem.success ? (problem.data.fields?.["confirm"] ?? problem.data.message) : "That did not work. Try again.");
        return;
      }
      const scheduled = expect(reply, deletionResponseContract, "/profile/delete").deletion;
      if (scheduled) session.setProfile({ ...session.profile, deletion: { ...scheduled, reason: "requested" } });
    } catch (caught) {
      setError(unreachable(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Delete your account">
      <div class="stack">
      <p class="note">
        Your profile, your files and everything you have made here are deleted two weeks after you ask, and you can change your
        mind until then. Records the law makes us keep, such as invoices, stay, with nothing on them that says who you were.
      </p>
      {open ? (
        <form
          class="stack"
          onSubmit={(event) => {
            event.preventDefault();
            void confirm();
          }}
        >
          <div class="field">
            <label for="delc">Type DELETE to confirm</label>
            <input
              class="input"
              id="delc"
              autocomplete="off"
              value={typed}
              aria-invalid={error ? "true" : undefined}
              onInput={(event) => setTyped((event.currentTarget as HTMLInputElement).value)}
            />
          </div>
          {error ? (
            <p class="notice bad" role="alert">
              {error}
            </p>
          ) : null}
          <div class="row">
            <button type="submit" class="btn danger" disabled={busy || typed.trim() !== "DELETE"}>
              {busy ? "Scheduling..." : "Delete my account"}
            </button>
            <Button variant="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <Button onClick={() => setOpen(true)}>Delete my account...</Button>
        </div>
      )}
      </div>
    </Card>
  );
}
